import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Sparkles, Image as ImageIcon, Film, Trash2, Upload, Loader2, Play, X, Flame } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import HearthVesta from "@/pages/vesta/HearthVesta";

// Anteprima del salva schermo: riproduce lo stesso ciclo della vista schermo (logo/nome → filmato → TORRI → Vesta).
function Anteprima({ nome, logoUrl, logoBg, videoUrl, onClose }) {
  const frames = [...(videoUrl ? ["video"] : []), "torri", "vesta", "cliente"];
  const [idx, setIdx] = useState(0);
  const [vidSafety, setVidSafety] = useState(null);
  const f = frames[Math.min(idx, frames.length - 1)];
  const next = () => setIdx((i) => (i + 1) % frames.length);
  const onVidMeta = (e) => { const dd = e.target?.duration; setVidSafety(Number.isFinite(dd) && dd > 0 ? (dd + 6) * 1000 : 300000); };
  useEffect(() => { setVidSafety(null); }, [idx]);
  useEffect(() => {
    const d = f === "video" ? (vidSafety ?? 300000) : f === "vesta" ? 6000 : 4500; // il video avanza con onEnded; la rete di sicurezza segue la durata reale del filmato
    const t = setTimeout(next, d);
    return () => clearTimeout(t);
  }, [idx, vidSafety]); // eslint-disable-line react-hooks/exhaustive-deps

  const frameBg = (f === "cliente" && logoUrl && logoBg) ? logoBg : "#000000";
  const lum = frameBg.length === 7 ? (0.299 * parseInt(frameBg.slice(1, 3), 16) + 0.587 * parseInt(frameBg.slice(3, 5), 16) + 0.114 * parseInt(frameBg.slice(5, 7), 16)) : 0;
  const light = lum > 170;
  const chip = light ? "bg-black/10 text-black/60 ring-black/10" : "bg-white/10 text-white/70 ring-white/15";
  const hint = light ? "text-black/40" : "text-white/50";

  return createPortal(
    <div className="cliente-font fixed inset-0 z-[130] flex items-center justify-center" data-testid="salva-schermo-anteprima" style={{ backgroundColor: frameBg }} onClick={onClose}>
      <style>{`@keyframes ssFrame{0%{opacity:0;transform:scale(.96)}60%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}`}</style>
      <span className={`absolute left-1/2 top-5 -translate-x-1/2 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ring-1 ${chip}`}>Anteprima salva schermo</span>
      <button type="button" onClick={onClose} className={`absolute right-4 top-4 z-10 rounded-full p-2.5 ring-1 ${chip}`} aria-label="Chiudi anteprima" data-testid="anteprima-close"><X className="h-5 w-5" /></button>
      <div key={idx} className="absolute flex max-w-[85vw] flex-col items-center px-6" style={{ animation: "ssFrame 0.6s cubic-bezier(0.4,0,0.2,1)" }} data-testid={`anteprima-${f}`}>
        {f === "cliente" && (logoUrl
          ? <img src={logoUrl} alt={nome || "Cliente"} className="max-h-[26vh] w-auto max-w-[55vw] object-contain" />
          : <span className="max-w-[80vw] text-center text-2xl font-extrabold tracking-tight text-white sm:text-3xl">{nome}</span>)}
        {f === "video" && <video src={videoUrl} autoPlay playsInline preload="auto" onLoadedMetadata={onVidMeta} onEnded={next} onError={next} className="max-h-[80vh] max-w-[85vw] rounded-2xl shadow-2xl" />}
        {f === "torri" && <img src="/torri-logo-full-dark.png" alt="TORRI" className="h-12 w-auto object-contain sm:h-16" />}
        {f === "vesta" && <span className="text-2xl font-extrabold tracking-[0.2em] text-white sm:text-4xl"><HearthVesta /></span>}
      </div>
      <span className={`absolute bottom-5 left-1/2 -translate-x-1/2 text-xs ${hint}`}>Tocca per chiudere</span>
    </div>,
    document.body,
  );
}

// Versione Pro: il cliente personalizza il salva schermo con il proprio logo e un filmato aziendale.
export function ClienteSalvaSchermo({ src, hasLogo, hasVideo, nome, logoUrl, logoBg, videoUrl, bollettinoAuto, onChange }) {
  const [logo, setLogo] = useState(!!hasLogo);
  const [video, setVideo] = useState(!!hasVideo);
  const [boll, setBoll] = useState(bollettinoAuto !== false);
  const [busyBoll, setBusyBoll] = useState(false);
  const [busyLogo, setBusyLogo] = useState(false);
  const [vidProg, setVidProg] = useState(null); // null = idle, 0..100 = caricamento
  const [preview, setPreview] = useState(false);
  const logoInp = useRef(null);
  const vidInp = useRef(null);

  const upLogo = async (e) => {
    const file = e.target.files?.[0]; if (logoInp.current) logoInp.current.value = ""; if (!file) return;
    setBusyLogo(true);
    try { await src.logoUp(file); toast.success("Logo caricato"); setLogo(true); onChange?.(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore nel caricamento del logo"); }
    finally { setBusyLogo(false); }
  };
  const delLogo = async () => {
    setBusyLogo(true);
    try { await src.logoDel(); toast.success("Logo rimosso"); setLogo(false); onChange?.(); }
    catch { toast.error("Errore"); } finally { setBusyLogo(false); }
  };

  const upVideo = async (e) => {
    const file = e.target.files?.[0]; if (vidInp.current) vidInp.current.value = ""; if (!file) return;
    if (!/^video\/(mp4|webm|quicktime)$/.test(file.type)) { toast.error("Usa un filmato MP4, WEBM o MOV"); return; }
    if (file.size > 80 * 1024 * 1024) { toast.error("Filmato troppo grande (max 80 MB)"); return; }
    // controllo durata (max 2 min)
    try {
      const dur = await new Promise((res) => { const v = document.createElement("video"); v.preload = "metadata"; v.onloadedmetadata = () => res(v.duration); v.onerror = () => res(0); v.src = URL.createObjectURL(file); });
      if (dur && dur > 122) { toast.error("Il filmato supera i 2 minuti"); return; }
    } catch { /* se non leggibile, lascio decidere al backend */ }
    setVidProg(0);
    try {
      const { upload_id } = await src.videoInit();
      const CH = 4 * 1024 * 1024;
      let idx = 0;
      for (let off = 0; off < file.size; off += CH) {
        await src.videoChunk(upload_id, idx, file.slice(off, Math.min(off + CH, file.size)));
        idx += 1;
        setVidProg(Math.min(99, Math.round(((off + CH) / file.size) * 100)));
      }
      await src.videoFinish(upload_id, file.type || "video/mp4");
      setVidProg(100); setVideo(true); toast.success("Filmato caricato"); onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nel caricamento del filmato"); }
    finally { setTimeout(() => setVidProg(null), 600); }
  };
  const delVideo = async () => {
    try { await src.videoDel(); toast.success("Filmato rimosso"); setVideo(false); onChange?.(); }
    catch { toast.error("Errore"); }
  };

  const toggleBoll = async (val) => {
    setBoll(val); setBusyBoll(true);
    try { await src.salvaSchermoOpz({ bollettino_auto: val }); toast.success(val ? "Bollettino vocale automatico attivato" : "Bollettino vocale automatico disattivato"); onChange?.(); }
    catch { setBoll(!val); toast.error("Errore"); }
    finally { setBusyBoll(false); }
  };

  return (
    <div className="space-y-3 rounded-2xl bg-[#FBF3EE] p-4 ring-1 ring-[#C17F59]/20" data-testid="cliente-salva-schermo">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#C17F59]" />
        <div className="min-w-0 flex-1">
          <b className="block text-slate-800">Personalizza il salva schermo</b>
          <span className="text-xs text-slate-500">Il tuo logo e un filmato aziendale appariranno a rotazione sullo schermo, insieme ai loghi TORRI e Vesta.</span>
        </div>
        <button type="button" onClick={() => setPreview(true)} disabled={vidProg != null} data-testid="salva-schermo-anteprima-open"
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-[#C17F59] ring-1 ring-[#C17F59]/30 transition-transform hover:scale-105 disabled:opacity-50">
          <Play className="h-3.5 w-3.5" />Anteprima</button>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-[#C17F59]/15 pt-3">
        <span className="flex items-center gap-2 text-sm text-slate-600"><ImageIcon className="h-4 w-4 text-[#C17F59]" />{logo ? "Logo caricato" : "Nessun logo"}</span>
        <span className="flex gap-1.5">
          <input ref={logoInp} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={upLogo} data-testid="salva-schermo-logo-input" />
          <button type="button" onClick={() => logoInp.current?.click()} disabled={busyLogo} data-testid="salva-schermo-logo-upload"
            className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-xs font-bold text-[#C17F59] ring-1 ring-[#C17F59]/30 disabled:opacity-50">
            {busyLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Carica</button>
          {logo && <button type="button" onClick={delLogo} disabled={busyLogo} data-testid="salva-schermo-logo-remove" className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-[#C17F59]/15 pt-3">
        <span className="flex items-center gap-2 text-sm text-slate-600"><Film className="h-4 w-4 text-[#C17F59]" />{vidProg != null ? `Caricamento ${vidProg}%` : (video ? "Filmato caricato" : "Nessun filmato")}<span className="text-xs text-slate-400">(MP4/WEBM/MOV, max 2 min · 80 MB)</span></span>
        <span className="flex gap-1.5">
          <input ref={vidInp} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={upVideo} data-testid="salva-schermo-video-input" />
          <button type="button" onClick={() => vidInp.current?.click()} disabled={vidProg != null} data-testid="salva-schermo-video-upload"
            className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-xs font-bold text-[#C17F59] ring-1 ring-[#C17F59]/30 disabled:opacity-50">
            {vidProg != null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Carica</button>
          {video && vidProg == null && <button type="button" onClick={delVideo} data-testid="salva-schermo-video-remove" className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-[#C17F59]/15 pt-3">
        <span className="flex min-w-0 items-center gap-2 text-sm text-slate-600"><Flame className="h-4 w-4 shrink-0 text-[#C17F59]" /><span className="min-w-0"><span className="block">Bollettino vocale di Vesta</span><span className="block text-xs text-slate-400">Annuncio automatico ogni ora sullo schermo (ore 9–19)</span></span></span>
        <Switch checked={boll} disabled={busyBoll} onCheckedChange={toggleBoll} data-testid="salva-schermo-bollettino-toggle" />
      </div>

      {preview && <Anteprima nome={nome} logoUrl={logo ? logoUrl : null} logoBg={logoBg} videoUrl={video ? videoUrl : null} onClose={() => setPreview(false)} />}
    </div>
  );
}
